import React from 'react'
import { ApiTableColumn, FetchProps } from '@e3dc-react/shell/Elements/ApiView'
import { ListProps } from '@e3dc-react/shell/Elements/ApiView/ApiView'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Installation } from 'src/Libs/installations/models'
import InstallationLink from 'src/Elements/Installations/InstallationLink'

interface UseUserInstallationsTableData {
  fetchProps: FetchProps
  listProps: ListProps<Installation>
  columns: ApiTableColumn<Installation>[]
}

/**
 * Table data for table of installations owned by a user
 * @param userId
 */
export const useUserInstallationsTableData = (userId?: string): UseUserInstallationsTableData => {
  const { t } = useCustomTranslation()

  const getFullAddress = (item: Installation): string => {
    const street = [`${item.streetName || ''} `, `${item.streetNumber || ''}`].join('')
    const city = [`${item.zip || ''} `, `${item.city || ''}`].join('')

    return [street, city].join(',')
  }

  const fetchProps: FetchProps = {
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    url: `installations`,
    filter: `ownerAccountId=${userId}`,
  }

  const listProps: ListProps<Installation> = {
    primary: item => item.id,
    secondary: item => getFullAddress(item),
  }

  const emptySymbol = '-'

  const columns: ApiTableColumn<Installation>[] = [
    {
      name: 'id',
      label: t('common.id'),
      value: (item: Installation) => {
        return <InstallationLink installation={item} />
      },
    },
    {
      name: 'countryCode',
      label: t('common.country'),
      value: ({ countryCode }) => (countryCode ? t(`common.countries.short.${countryCode}`) : emptySymbol),
    },
    {
      name: 'zip',
      label: t('common.zip'),
      value: ({ zip }) => zip ?? emptySymbol,
    },
    {
      name: 'city',
      label: t('common.city'),
      value: ({ city }) => city ?? emptySymbol,
    },
    {
      name: 'streetName',
      label: t('common.address'),
      value: ({ streetName, streetNumber }) =>
        streetName || streetNumber ? [`${streetName || ''} `, `${streetNumber || ''}`].join('') : emptySymbol,
    },
  ]

  return { fetchProps, columns, listProps }
}
